import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faUserShield } from '@fortawesome/free-solid-svg-icons';
import { listUsers } from '../../redux-components/actions/userActions';
import { listApiKeys } from '../../redux-components/actions/apiKeyActions';
import AdminUserApiKeys, { keysForUser } from './AdminUserApiKeys';
import { FEATURES, ROLES, normalizeRole } from '../../permissions';
import Loader from '../Loader';
import Message from '../Message';
import { customSwal } from '../CustomSwal';
import { updateUserRole } from './adminUsersApi';
import useRefetchOnFocus from '../../hooks/useRefetchOnFocus';

const ROLE_LABELS = {
  [ROLES.STANDARD]: 'Standard',
  [ROLES.CMS]: 'CMS',
  [ROLES.SPECIAL]: 'Special',
};

const ROLE_DESCRIPTIONS = {
  [ROLES.STANDARD]: 'The normal app: dashboard, notes, shortcuts, reminders and grocery.',
  [ROLES.CMS]: 'Website customers. The console only, and they start there instead of the dashboard.',
  [ROLES.SPECIAL]: 'Standard plus whatever is still in testing, currently the checklist.',
};

const ROLE_BADGES = {
  [ROLES.STANDARD]: 'bg-gray-600/40 text-gray-200',
  [ROLES.CMS]: 'bg-blue-500/20 text-blue-200',
  [ROLES.SPECIAL]: 'bg-emerald-500/20 text-emerald-200',
};

// What a role can reach, read from the same map the server enforces
const featuresFor = (role) => Object.entries(FEATURES)
  .filter(([, roles]) => roles.includes(role))
  .map(([feature]) => feature);

const AdminUsers = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userLogin);
  const { loading, error, users } = useSelector((state) => state.userList);
  const { apiKeys } = useSelector((state) => state.apiKeyList);

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  // Roles changed here, so the table updates without refetching everyone
  const [roleOverrides, setRoleOverrides] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    dispatch(listUsers());
    dispatch(listApiKeys());
  }, [dispatch]);

  // Roles and keys change from other places; coming back to the tab should show the current state.
  // Silent: the user didn't ask for this, so no spinner — the table just updates if anything changed.
  useRefetchOnFocus(() => {
    dispatch(listUsers({ background: true }));
    dispatch(listApiKeys({ background: true }));
  });

  const roleOf = (user) => roleOverrides[user._id] || normalizeRole(user.role);

  const query = search.trim().toLowerCase();
  const visibleUsers = useMemo(() => {
    if (!query) return users || [];
    return (users || []).filter((user) => (
      `${user.username} ${user.email}`.toLowerCase().includes(query)
    ));
  }, [users, query]);

  const selected = (users || []).find((user) => user._id === selectedId) || null;

  const handleRoleChange = async (user, role) => {
    const previous = roleOf(user);
    if (role === previous) return;

    setRoleOverrides((prev) => ({ ...prev, [user._id]: role }));
    setSavingId(user._id);

    try {
      await updateUserRole(user._id, role, userInfo.token);
    } catch (err) {
      // A control that stays on the new value is a lie about what the account can do
      setRoleOverrides((prev) => ({ ...prev, [user._id]: previous }));
      customSwal.fire({ icon: 'error', title: "Couldn't change role", text: err.message });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <section className="w-full p-2 text-left sm:p-4">
      <div className="mb-4">
        <h2 className="text-2xl font-bold">Users</h2>
        <p className="mt-0.5 text-sm text-gray-400">
          {users?.length || 0} {users?.length === 1 ? 'account' : 'accounts'}. Admin is set in the
          database, not here.
        </p>
      </div>

      <div className="relative mb-4 max-w-md">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by username or email"
          aria-label="Search users"
          className="w-full rounded-md bg-gray-800 py-2 pl-9 pr-3 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : visibleUsers.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-600 px-4 py-8 text-center text-sm text-gray-400">
          {query ? `No account matches "${search.trim()}"` : 'No accounts yet.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-700/70 bg-gray-900/70">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b border-gray-700/70 text-xs uppercase tracking-wide text-gray-400">
              <tr>
                <th className="px-4 py-2 font-medium">User</th>
                <th className="px-4 py-2 font-medium">Role</th>
                <th className="px-4 py-2 font-medium">API keys</th>
                <th className="px-4 py-2 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((user) => {
                const role = roleOf(user);
                const active = user._id === selectedId;

                return (
                  <tr
                    key={user._id}
                    onClick={() => setSelectedId(active ? null : user._id)}
                    className={`cursor-pointer border-t border-gray-700/70 transition-colors ${
                      active ? 'bg-blue-600/10' : 'hover:bg-gray-800/70'
                    }`}
                  >
                    <td className="px-4 py-2">
                      <span className="flex items-center gap-2 font-medium">
                        {user.username}
                        {user.isAdmin && (
                          <FontAwesomeIcon icon={faUserShield} title="Admin" className="text-yellow-300" />
                        )}
                      </span>
                      <span className="block text-xs text-gray-400">{user.email}</span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${ROLE_BADGES[role]}`}>
                        {ROLE_LABELS[role]}
                      </span>
                      {user.isAdmin && (
                        <span className="ml-2 text-xs text-gray-400">+ admin</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-400">
                      {keysForUser(apiKeys, user._id).length || '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-400">
                      {user.createdAt ? moment(user.createdAt).format('ll') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected ? (
        <div className="mt-4 rounded-lg border border-gray-700/70 bg-gray-900/70 p-4">
          <h3 className="text-lg font-semibold">{selected.username}</h3>
          <p className="text-xs text-gray-400">{selected.email} · {selected._id}</p>

          <div className="mt-4 max-w-md">
            <label htmlFor="user-role" className="block font-medium">Role:</label>
            <p className="mb-2 text-xs text-gray-400">Saved as soon as you pick one.</p>
            <select
              id="user-role"
              value={roleOf(selected)}
              disabled={savingId === selected._id}
              onChange={(e) => handleRoleChange(selected, e.target.value)}
              className="w-full rounded-md bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {Object.values(ROLES).map((role) => (
                <option key={role} value={role}>{ROLE_LABELS[role]}</option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-400">{ROLE_DESCRIPTIONS[roleOf(selected)]}</p>
          </div>

          <div className="mt-4">
            <p className="text-xs uppercase tracking-wide text-gray-400">Can use</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {featuresFor(roleOf(selected)).map((feature) => (
                <span key={feature} className="rounded bg-gray-700/70 px-2 py-0.5 text-xs text-gray-200">
                  {feature}
                </span>
              ))}
              {selected.isAdmin && (
                <span className="rounded bg-yellow-500/20 px-2 py-0.5 text-xs text-yellow-200">
                  admin: everything
                </span>
              )}
            </div>
          </div>

          <p className="mt-4 text-xs text-gray-400">
            Changing a role takes effect immediately on the server. Their menu updates the next time
            they log in.
          </p>

          <AdminUserApiKeys user={selected} />
        </div>
      ) : (
        <p className="mt-4 text-sm text-gray-400">Pick an account to see or change what it can do.</p>
      )}
    </section>
  );
};

export default AdminUsers;
